/**
 * ProductPickerModal — zaawansowany picker towaru (F2, plan „advanced product-picker
 * modal + multi-add wiring"). Otwierany z przycisku „…" na `ProductSearchPicker`
 * (`onOpenAdvancedPicker`) — rozszerza prosty combobox o wyszukiwarkę + wielowybór
 * magazynów + przełączniki + paginowaną tabelę ze stanami magazynowymi (mirror
 * pickera z ESITE.WorkFlow).
 *
 * MULTI-select: zaznaczenia trzymane w `Map<string, ProductPickerRow>` (nie w Set
 * id-ków) — Map przetrwa zmianę strony/filtra bez utraty danych wiersza potrzebnych
 * do `onConfirm`. `onConfirm` ZAWSZE dostaje tablicę (nawet przy jednym zaznaczeniu)
 * — decyzję co zrobić z 1 vs N towarami podejmuje wołający (`DocumentItemsEditor`).
 *
 * Zapytanie: GET /products?withStock=true&... (kontrakt patrz nagłówek
 * `apps/api/app/api/products/route.ts` — B4). `active` semantyka: toggle „Pokaż
 * nieaktywne" WYŁĄCZONY (domyślnie) → wysyłamy `active=true` (tylko aktywne);
 * WŁĄCZONY → param pomijamy całkowicie (serwer bez `active` zwraca aktywne I
 * nieaktywne — nie ma potrzeby osobnego `active=false`, bo nie ma trybu „tylko
 * nieaktywne").
 *
 * Degradacja modułowa (402/403 — moduł katalogu nieaktywny / brak
 * uprawnienia) traktowana jak stan „brak" w `ProductSearchPicker`/`useWarehouses`,
 * ale tu z WŁASNYM, odróżnialnym stanem UI (`gated` w wyniku query) — inaczej
 * niemożliwe byłoby odróżnić "moduł nieaktywny" od "katalog faktycznie pusty"
 * (obie sytuacje degradują do pustej listy identycznego kształtu).
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Search, X } from 'lucide-react';

import { TablePagination } from '@/components/configurable-table/TablePagination';
import { Modal } from '@/components/Modal';
import { type ProductPickResult } from '@/components/ui/ProductSearchPicker';
import { api } from '@/lib/api';
import { isModuleGatingError, MODULE_GATED_LIST_STALE_TIME_MS } from '@/lib/module-gating';

import { WarehouseMultiSelect } from './WarehouseMultiSelect';

/** Wiersz listy w trybie pickera (`withStock=true`) — superset `ProductPickResult`
 * o agregaty stanów magazynowych + `isActive` (do wyszarzenia wierszy nieaktywnych). */
export interface ProductPickerRow {
  id: string;
  code: string | null;
  name: string;
  unit: string | null;
  basePrice: string | null;
  vatRate: string | null;
  pkwiu: string | null;
  cn: string | null;
  isActive: boolean;
  stockQuantity: string;
  stockReserved: string;
  stockIncoming: string;
}

interface ProductsPickerListResponse {
  products: ProductPickerRow[];
  total: number;
  limit: number;
  offset: number;
}

/** Wynik query rozszerzony o flagę degradacji modułowej — patrz nagłówek pliku. */
interface ProductsPickerQueryResult extends ProductsPickerListResponse {
  gated: boolean;
}

export interface ProductPickerModalProps {
  open: boolean;
  onClose: () => void;
  companyId?: string | null;
  /** ZAWSZE tablica, niezależnie od liczby zaznaczonych wierszy (nawet 1). */
  onConfirm: (products: ProductPickResult[]) => void;
}

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 250;
const TEST_ID = 'product-picker-modal';

function toProductPickResult(row: ProductPickerRow): ProductPickResult {
  return {
    id: row.id,
    code: row.code,
    name: row.name,
    unit: row.unit,
    basePrice: row.basePrice,
    vatRate: row.vatRate,
    pkwiu: row.pkwiu,
    cn: row.cn,
  };
}

export function ProductPickerModal({
  open,
  onClose,
  companyId,
  onConfirm,
}: ProductPickerModalProps) {
  const { t } = useTranslation();

  const [inputValue, setInputValue] = useState('');
  const [search, setSearch] = useState('');
  const [warehouseIds, setWarehouseIds] = useState<string[]>([]);
  const [onlyAvailable, setOnlyAvailable] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [page, setPage] = useState(0);
  const [selectedRows, setSelectedRows] = useState<Map<string, ProductPickerRow>>(new Map());
  const searchRef = useRef<HTMLInputElement>(null);

  // Debounce input → search (feeds queryKey), mirror ProductSearchPicker.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(inputValue), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [inputValue]);

  const warehouseKey = [...warehouseIds].sort().join(',');

  // Zmiana dowolnego filtra wraca na pierwszą stronę — inaczej user mógłby
  // zawęzić wyniki i utknąć na stronie, która przestała istnieć.
  useEffect(() => {
    setPage(0);
  }, [search, warehouseKey, onlyAvailable, includeInactive]);

  // Reset PEŁNEGO stanu lokalnego przy zamknięciu — kolejne otwarcie zawsze
  // startuje "na czysto" (mirror DocumentTypePickerModal).
  useEffect(() => {
    if (!open) {
      setInputValue('');
      setSearch('');
      setWarehouseIds([]);
      setOnlyAvailable(false);
      setIncludeInactive(false);
      setPage(0);
      setSelectedRows(new Map());
    }
  }, [open]);

  const { data, isLoading, isError } = useQuery<ProductsPickerQueryResult>({
    queryKey: [
      'products-picker',
      companyId ?? null,
      search,
      warehouseKey,
      onlyAvailable,
      includeInactive,
      page,
    ],
    queryFn: async () => {
      const params = new URLSearchParams({
        withStock: 'true',
        limit: String(PAGE_SIZE),
        offset: String(page * PAGE_SIZE),
      });
      if (search) params.set('q', search);
      if (companyId) params.set('companyId', companyId);
      if (warehouseIds.length > 0) params.set('warehouseIds', warehouseKey);
      if (onlyAvailable) params.set('onlyAvailable', 'true');
      // "Pokaż nieaktywne" WYŁĄCZONY (domyślnie) → tylko aktywne (`active=true`).
      // WŁĄCZONY → param pomijamy — serwer bez `active` zwraca oba stany.
      if (!includeInactive) params.set('active', 'true');
      try {
        const res = await api.get<ProductsPickerListResponse>(`/products?${params.toString()}`);
        return { ...res.data, gated: false };
      } catch (err) {
        // Moduł katalogu nieaktywny (402) albo brak uprawnienia (403) —
        // degradujemy do pustej listy z flagą `gated`, żeby UI mógł pokazać komunikat
        // odróżnialny od "katalog faktycznie pusty". INNE błędy (sieć, 500) rzucamy
        // dalej — mają wpaść w isError.
        if (isModuleGatingError(err)) {
          return {
            products: [],
            total: 0,
            limit: PAGE_SIZE,
            offset: page * PAGE_SIZE,
            gated: true,
          };
        }
        throw err;
      }
    },
    enabled: open,
    staleTime: MODULE_GATED_LIST_STALE_TIME_MS,
  });

  const rows = data?.products ?? [];
  const isGated = data?.gated ?? false;
  const hasActiveFilter =
    search.trim() !== '' || warehouseIds.length > 0 || onlyAvailable || includeInactive;

  function toggleRow(row: ProductPickerRow) {
    setSelectedRows((prev) => {
      const next = new Map(prev);
      if (next.has(row.id)) {
        next.delete(row.id);
      } else {
        next.set(row.id, row);
      }
      return next;
    });
  }

  function handleConfirm() {
    if (selectedRows.size === 0) return;
    onConfirm([...selectedRows.values()].map(toProductPickResult));
    onClose();
  }

  const footer = (
    <div className="flex w-full items-center justify-end gap-2">
      <button
        type="button"
        onClick={onClose}
        className="rounded-lg px-3 py-1.5 text-sm text-text-secondary hover:bg-bg-secondary"
        data-testid={`${TEST_ID}-cancel`}
      >
        {t('ui.productPickerModal.cancel')}
      </button>
      <button
        type="button"
        onClick={handleConfirm}
        disabled={selectedRows.size === 0}
        className="rounded-lg bg-accent-600 px-3 py-1.5 text-sm text-white hover:bg-accent-700 disabled:cursor-not-allowed disabled:opacity-50"
        data-testid={`${TEST_ID}-confirm`}
      >
        {t('ui.productPickerModal.confirm')}
      </button>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('ui.productPickerModal.title')}
      size="2xl"
      footer={footer}
    >
      <div data-testid={TEST_ID} className="flex flex-col gap-3">
        {/* Pasek filtrów: szukaj + magazyny + przełączniki. */}
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[220px] flex-1">
            <label className="mb-1 block text-xs font-medium text-text-secondary">
              {t('ui.productPickerModal.searchPlaceholder')}
            </label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-muted" />
              <input
                ref={searchRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder={t('ui.productPickerModal.searchPlaceholder')}
                className="h-[42px] w-full rounded-md border border-border-primary bg-bg-primary pl-8 pr-8 text-sm text-text-primary placeholder-text-muted focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500"
                data-testid={`${TEST_ID}-search`}
              />
              {inputValue && (
                <button
                  type="button"
                  onClick={() => {
                    setInputValue('');
                    searchRef.current?.focus();
                  }}
                  aria-label={t('ui.productPicker.clearSearchAriaLabel')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted hover:text-danger-600"
                  data-testid={`${TEST_ID}-search-clear`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          <div className="min-w-[240px]">
            <label className="mb-1 block text-xs font-medium text-text-secondary">
              {t('ui.productPickerModal.warehouseLabel')}
            </label>
            <WarehouseMultiSelect
              companyId={companyId}
              value={warehouseIds}
              onChange={setWarehouseIds}
              data-testid={`${TEST_ID}-warehouse-select`}
            />
          </div>

          <label className="flex items-center gap-2 pb-1.5 text-sm text-text-secondary">
            <input
              type="checkbox"
              checked={onlyAvailable}
              onChange={(e) => setOnlyAvailable(e.target.checked)}
              className="h-4 w-4 rounded border-border-primary text-accent-600 focus:ring-accent-500"
              data-testid={`${TEST_ID}-toggle-only-available`}
            />
            {t('ui.productPickerModal.onlyAvailableToggle')}
          </label>

          <label className="flex items-center gap-2 pb-1.5 text-sm text-text-secondary">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
              className="h-4 w-4 rounded border-border-primary text-accent-600 focus:ring-accent-500"
              data-testid={`${TEST_ID}-toggle-include-inactive`}
            />
            {t('ui.productPickerModal.includeInactiveToggle')}
          </label>
        </div>

        {/* Chip "N zaznaczonych" — trwały, niezależny od strony/filtra (Map przetrwa oba). */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-text-secondary" data-testid={`${TEST_ID}-selected-count`}>
            {t('ui.productPickerModal.selectedCount', { count: selectedRows.size })}
          </span>
          <button
            type="button"
            onClick={() => setSelectedRows(new Map())}
            disabled={selectedRows.size === 0}
            className="text-xs text-accent-600 hover:underline disabled:cursor-not-allowed disabled:text-text-muted disabled:no-underline"
            data-testid={`${TEST_ID}-clear-selection`}
          >
            {t('ui.productPickerModal.clearSelection')}
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-8" data-testid={`${TEST_ID}-loading`}>
            <Loader2 className="h-5 w-5 animate-spin text-text-muted" />
            <span className="ml-2 text-sm text-text-muted">
              {t('ui.productPickerModal.loading')}
            </span>
          </div>
        ) : isError ? (
          <p
            className="px-3 py-8 text-center text-sm text-danger-600"
            data-testid={`${TEST_ID}-error`}
          >
            {t('ui.productPickerModal.loadError')}
          </p>
        ) : isGated ? (
          <p
            className="px-3 py-8 text-center text-sm text-text-muted"
            data-testid={`${TEST_ID}-gated`}
          >
            {t('ui.productPickerModal.gatedHint')}
          </p>
        ) : rows.length === 0 ? (
          <p
            className="px-3 py-8 text-center text-sm text-text-muted"
            data-testid={`${TEST_ID}-empty`}
          >
            {hasActiveFilter
              ? t('ui.productPickerModal.noResults')
              : t('ui.productPickerModal.emptyState')}
          </p>
        ) : (
          <div className="overflow-x-auto rounded border border-border-primary">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-primary bg-bg-secondary text-left text-xs font-medium uppercase tracking-wide text-text-tertiary">
                  <th className="w-8 px-2 py-2" scope="col" />
                  <th className="px-2 py-2" scope="col">
                    {t('ui.productPickerModal.columns.code')}
                  </th>
                  <th className="px-2 py-2" scope="col">
                    {t('ui.productPickerModal.columns.name')}
                  </th>
                  <th className="px-2 py-2" scope="col">
                    {t('ui.productPickerModal.columns.unit')}
                  </th>
                  <th className="px-2 py-2 text-right" scope="col">
                    {t('ui.productPickerModal.columns.price')}
                  </th>
                  <th className="px-2 py-2 text-right" scope="col">
                    {t('ui.productPickerModal.columns.stock')}
                  </th>
                  <th className="px-2 py-2 text-right" scope="col">
                    {t('ui.productPickerModal.columns.incoming')}
                  </th>
                  <th className="px-2 py-2 text-right" scope="col">
                    {t('ui.productPickerModal.columns.reserved')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const isSelected = selectedRows.has(row.id);
                  return (
                    <tr
                      key={row.id}
                      onClick={() => toggleRow(row)}
                      className={[
                        'cursor-pointer border-b border-border-primary last:border-0 hover:bg-bg-secondary',
                        row.isActive ? 'text-text-primary' : 'italic text-text-muted',
                        isSelected ? 'bg-accent-50 dark:bg-accent-900/20' : '',
                      ].join(' ')}
                      data-testid={`${TEST_ID}-row-${row.id}`}
                    >
                      <td className="px-2 py-1.5">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleRow(row)}
                          className="h-4 w-4 rounded border-border-primary text-accent-600 focus:ring-accent-500"
                          data-testid={`${TEST_ID}-row-${row.id}-select`}
                        />
                      </td>
                      <td className="px-2 py-1.5">{row.code ?? '—'}</td>
                      <td className="px-2 py-1.5">{row.name}</td>
                      <td className="px-2 py-1.5">{row.unit ?? '—'}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {row.basePrice ?? '—'}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{row.stockQuantity}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{row.stockIncoming}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{row.stockReserved}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!isLoading && !isError && !isGated && (data?.total ?? 0) > 0 && (
          <TablePagination
            page={page}
            pageSize={PAGE_SIZE}
            totalItems={data?.total ?? 0}
            onPageChange={setPage}
          />
        )}
      </div>
    </Modal>
  );
}
